/* eslint-disable no-loop-func */
import React, { useState, useEffect } from 'react'

import { Spinner, Typography } from '@telavita-core/react-design-kit'

import { SpecialitiesSelector } from 'components'

import * as S from './styles'

import { ReactComponent as AddCircle } from 'static/svg/add-circle.svg'
import { useDispatch, useSelector } from 'react-redux'
import { INACTIVE } from 'settings/_personSettings'
import { IAuthState } from 'modules/FeatureFlags/Unleash/types'
import { getLastValidityDetail } from 'services/lastValidityDetail'
import { getTreatmentStatus } from 'services/treatments'
import { getCorporateEligibility } from 'services/corporateEligibilityService'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { CreditsCard } from 'components/CreditsCard/CreditsCard'
import { isAfter, format } from 'date-fns'
import LinkPlanModal from 'components/LinkPlanModal'
import { listPlanGroups } from 'services/planGroups/listPlanGroups'
import { useQuery } from '@tanstack/react-query'
import { linkPlan } from 'services/linkPlan'
import { AUTH_ADD_PLAN } from 'redux/actions/_actionTypes'
import { createMedicalRequest } from 'services/homeService'
import { getPatientPlans, PlansData } from 'services/patients/getPatientPlans'

const {
  CARE_PLUS,
  CORPORATE_LIMITED,
  CORPORATE_UNLIMITED,
  ENTERPRISE,
  VITTA,
  CAPITATION
} = PlanGroupCategoryEnum

interface LinkPlanData {
  patientName?: string
  planCode: string
  userName: string
  cardNumber?: string
}

export function PatientPlans(): JSX.Element {

  const dispatch = useDispatch()

  const [patientPlans, setPatientPlans] = useState([])

  const { data: planGroups } = useQuery(['planGroups'], () =>
    listPlanGroups().then(response => response)
  )

  const username = useSelector((state: IAuthState) => state?.auth?.userData?.username)
  const patientPersonData = useSelector((state: IAuthState) => state?.auth?.userData?.person_general)

  const [loadingPlansEligibility, setLoadingPlansEligibility] = useState(true)
  const [plansEligibility, setPlansEligibility] = useState([])
  const [openLinkPlanModal, setOpenLinkPlanModal] = useState(false)

  const orderedPlansEligibility = plansEligibility.sort((a, b) => {
    const aPlanGroupName = a.plan_group.name.toLowerCase()
    const bplanGroupName = b.plan_group.name.toLowerCase()

    if (aPlanGroupName < bplanGroupName) {
      return -1
    }
    if (aPlanGroupName > bplanGroupName) {
      return 1
    }
    return 0
  })

  const fetchPlansEligibility = async (plans) => {
    if (plans.length <= 0) return setLoadingPlansEligibility(false)

    let plansEligibility = []
    
    const lastPlan = plans.slice(-1)[0]
    
    for await (const plan of plans) {
      const isCorporate = [CORPORATE_LIMITED, CORPORATE_UNLIMITED].includes(
        plan.plan_group?.category
      )

      const isEnterprise = ([CARE_PLUS, ENTERPRISE, CAPITATION]).includes(plan.plan_group?.category)

      const psychiatryEligibility = await getTreatmentStatus({
        userName: username,
        consultationType: 'PSIQ',
        planCode: plan.plan_code,
      })

      if (isCorporate) {
        await getCorporateEligibility({
          username,
          planCode: plan.plan_code,
        }).then((eligibility) => {
          plansEligibility = [...plansEligibility, {
            ...plan,
            eligibility,
            psychiatry: psychiatryEligibility,
            isCorporate: true,
          },]
        })
      } 
      
      if (isEnterprise) {        
        await getLastValidityDetail(username, plan.plan_code).then(
          (validityDetail) => {
            plansEligibility = [...plansEligibility, {
              ...plan,
              eligibility: validityDetail,
              psychiatry: psychiatryEligibility,
              isEnterprise: true
            },]
          }
        ).catch(err => {
          plansEligibility = [...plansEligibility, {
            ...plan,
            isActive: plan.status !== INACTIVE,
            eligibility: {},
            psychiatry: psychiatryEligibility,
            isEnterprise: true
          },]
        })
      } else {
        plansEligibility = [...plansEligibility, plan]
      }

      if (plan.plan_code === lastPlan.plan_code) {
        setLoadingPlansEligibility(false)
      }

    }
    
    setPlansEligibility(plansEligibility)
  }

  const handleRequestTreatementStatus = ({
    consultationType = 'PSIQ',
    planCode
  }) => {
    return new Promise((resolve, eject) => {
      getTreatmentStatus({
        userName: username,
        consultationType,
        planCode
      })
        .then(status => {
          resolve(status.cycleStatus)
        })
        .catch(() => {
          eject()
        })
    })
  }

  const handleCreateMedicReqest = async (planCode) => {
    await getLastValidityDetail(username, planCode).then(
      (validityDetail) => {

        setPlansEligibility(prevState => 
          prevState.map(plan => {
            if (plan.plan_code === planCode) {
              return {
                ...plan,
                eligibility: validityDetail
              }
            }

            return plan
          })  
        )
      }
    ).catch(err => {
      console.log(err)
      setPlansEligibility(prevState => 
        prevState.map(plan => {
          if (plan.plan_code === planCode) {
            return {
              ...plan,
              eligibility: {}
            }
          }

          return plan
        })  
      )
    })
  }

  const onClickRequestCreditsButton = (planCode: string) => {
    createMedicalRequest({
      planCode
    }).then(() => {
      fetchPlansEligibility(patientPlans)
    })
  }

  const renderCreditsCard = (plan) => {
    const isCareplus = [CARE_PLUS].includes(plan.plan_group.category)
    const isCassi = plan.plan_group.name === 'CASSI'
    const isUnlimited = plan.plan_group.category === CORPORATE_UNLIMITED
    const isCapitation = [CAPITATION].includes(plan.plan_group.category)

    const validDate = plan.isCorporate ? plan.eligibility?.expirationDate : new Date(plan.eligibility?.endDate)
    const expired = isAfter(new Date(), validDate)

    const solicitationDate = plan.eligibility?.dateCreated !== undefined 
      ? format(new Date(plan.eligibility?.dateCreated), 'dd/MM/yyyy')
      : ''

    return (
      <CreditsCard
        isEnterprise={plan.isEnterprise}
        isCorporate={plan.isCorporate}
        isCareplus={isCareplus}
        isCassi={isCassi}
        isCapitation={isCapitation}
        showRequestTreatment={!plan.isCorporate}
        hasButton={plan.eligibility?.enableSolicitation}
        planGroupName={plan.plan_group.name}
        planName={plan.name}
        showFullPlanName
        creditsStatus={plan.eligibility?.status}
        availableCredits={plan.eligibility?.availableCredits}
        maxCredits={plan.eligibility?.maxAppointment}
        enableDetails={plan.eligibility?.enableDetails}
        validDate={validDate}
        expired={expired}
        solicitationDate={solicitationDate}
        patientPlans={patientPlans}
        isUnlimited={isUnlimited}
        startDate={plan.eligibility?.startDate}
        corporateEligibility={plan.eligibility}
        planCode={plan.plan_code}
        cardNumber={plan.eligibility?.card_number}
        planGroupCode={plan.plan_group.code}
        reasonCode={plan.eligibility?.reasonCode}
        errorButton={false}
        loadingButton={false}
        loading={false}
        onClickRequestCreditsButton={() => onClickRequestCreditsButton(plan.plan_code)}
        forceUpdate={false}
        psychiatryEligibility={plan.psychiatry}
        psychiatryEligibilityLoading={false}
        onForceUpdate={() => handleCreateMedicReqest(plan.plan_code)}
        onRequestTreatement={handleRequestTreatementStatus}
        handleUpdateSelectedPlan={undefined} 
        setHasButton={undefined}      
        showPlanSwitch={false}
        showPsychiatry={plan.psychiatry_enabled}
        creditsPanelErrorMessage=''
        username={username}
        patientSelectedPlan={plan?.plan_code}
      />
    )
  }

  const onLinkPlan = async (data: LinkPlanData) => linkPlan(data)
    .then((newPlan) => {
      const plan = {
        ...newPlan,
        plan_code: newPlan.partner_plan_code,
        name: newPlan.partner_plan_name
      }
      setLoadingPlansEligibility(true)
      setPlansEligibility([])
      dispatch({ type: AUTH_ADD_PLAN, payload: { newPlan: plan } })
      fetchPlansEligibility([...patientPlans, plan])
    })

  useEffect(() => {
    
    getPatientPlans({
      username
    }).then((response: PlansData) => {
      const patientActivePlans = response.plans.filter(plan => !plan.is_particular && plan.status !== INACTIVE && plan.plan_group.category !== VITTA)
      
      fetchPlansEligibility(patientActivePlans)
      setPatientPlans(patientActivePlans)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  return (
    <S.Container>
      <S.Header>
        <Typography variant='header2' customClassName='PatientPlans__Title'>
          Meus convênios
        </Typography>
        <SpecialitiesSelector onSuccess={() => fetchPlansEligibility(patientPlans)}/>
      </S.Header>
      {loadingPlansEligibility ? (
        <Spinner customClassName='PatientPlans__Spinner'/>
      ) : (
        <>
          <S.PlansContainer
            style={{
              columnCount: orderedPlansEligibility.length === 0 ? 1 : 2,
            }}
          >
            {orderedPlansEligibility.map(plan => (
              <>
                {renderCreditsCard(plan)}
              </> 
            ))}
            <S.LinkPlanButton onClick={() => setOpenLinkPlanModal(true)}>
              <AddCircle />
              <Typography variant='content2' weight='bolder'>Adicionar convênio</Typography>
            </S.LinkPlanButton>
          </S.PlansContainer>
        </>
      )}
      {openLinkPlanModal && (
        <LinkPlanModal
          planGroups={planGroups?.filter(planGroup => planGroup.self_register)}
          onClose={() => setOpenLinkPlanModal(false)}
          patientCpf={patientPersonData.cpf}
          patientPlans={patientPlans.map(pPlan => ({
            partner_plan_code: pPlan.code,
            partner_plan_name: pPlan.name,
            status_code: pPlan.status,
            partner_plan_is_particular: pPlan.is_particular,
            plan_group: {
              code: pPlan.plan_group.code,
              name: pPlan.plan_group.name
            }
          }))}
          onLinkPlan={onLinkPlan}
          userName={username}
        />
      )}
    </S.Container>
  )
}